import { useMemo } from "react"
import { useDroppable } from "@dnd-kit/core"
import { Ban } from "lucide-react"
import { cn } from "@/lib/utils"
import { CalendarPieceCard } from "./CalendarPieceCard"
import {
  CalendarBlockChip,
  blockOverlapsHour,
  blockRangeLabel,
  blockVisible,
  instantBlocked,
} from "./CalendarBlockChip"
import type { CalendarSlot, CalendarChannel, CalendarHoliday, CalendarTemplateSlot, CalendarBlock } from "./types"

type Props = {
  anchor: string
  slots: CalendarSlot[]
  channels: CalendarChannel[]
  holidays: CalendarHoliday[]
  templateSlots: CalendarTemplateSlot[]
  blocks: CalendarBlock[]
  visibleChannels: Set<string>
}

// Day-of-week labels for the column headers. Week starts Sunday.
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// Default y-axis range — 6 AM to 10 PM. We render a coarse hourly grid
// and overlay piece cards at the minute level inside each hour cell.
// When a visible piece (publish or upload) lands outside this window in
// the current week, the range expands to include it (see hourRange()).
const DEFAULT_START_HOUR = 6
const DEFAULT_END_HOUR   = 22

export function CalendarWeekView({
  anchor,
  slots,
  channels,
  holidays,
  templateSlots,
  blocks,
  visibleChannels
}: Props) {
  const channelBySlug = useMemo(() => {
    const map = new Map<string, CalendarChannel>()
    channels.forEach((c) => map.set(c.slug, c))
    return map
  }, [channels])

  const weekStart = useMemo(() => {
    const d = new Date(`${anchor}T00:00:00`)
    const day = d.getDay()
    d.setDate(d.getDate() - day) // Sunday
    return d
  }, [anchor])

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart)
      d.setDate(weekStart.getDate() + i)
      return d
    })
  }, [weekStart])

  const hours = useMemo(() => {
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 7)
    let start = DEFAULT_START_HOUR
    let end   = DEFAULT_END_HOUR
    const consider = (iso: string | null) => {
      if (!iso) return
      const d = new Date(iso)
      if (d < weekStart || d >= weekEnd) return
      const h = d.getHours()
      if (h < start) start = h
      if (h > end)   end   = h
    }
    for (const s of slots) {
      if (!visibleChannels.has(s.channel)) continue
      consider(s.publish_at)
      if (s.use_platform_scheduling) consider(s.upload_at)
    }
    // Blocks expand the range too so a 5 AM block isn't invisible.
    for (const b of blocks) {
      if (!blockVisible(b, visibleChannels)) continue
      consider(b.starts_at)
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }, [weekStart, slots, blocks, visibleChannels])

  return (
    <div className="rounded-md border border-hairline bg-surface">
      <div className="grid grid-cols-[60px_repeat(7,_1fr)] border-b border-hairline">
        <div />
        {days.map((d, idx) => {
          const iso = d.toISOString().slice(0, 10)
          const holiday = holidays.find((h) => h.date === iso)
          const isToday = sameLocalDay(d, new Date())
          const isPast = isPastDay(d)
          return (
            <div
              key={iso}
              className={cn(
                "px-2 py-2 text-center text-xs",
                // Today and future days read as white; past days keep the
                // grid's gray surface (and stay dimmed).
                !isPast && "bg-page",
                isPast && "opacity-50"
              )}
            >
              <div className="text-ink-muted">{DAYS[idx]}</div>
              <div
                className={cn(
                  "text-base font-semibold",
                  // Mark today with a filled pill so it stays identifiable on
                  // the new white background.
                  isToday
                    ? "mx-auto flex h-7 w-7 items-center justify-center rounded-full bg-accent text-page"
                    : "text-ink-display"
                )}
              >
                {d.getDate()}
              </div>
              {holiday && (
                <div className="mt-0.5 truncate text-[10px] text-ink-muted" title={holiday.name}>
                  {holiday.name}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-[60px_repeat(7,_1fr)]">
        {hours.map((hour) => (
          <HourRow
            key={hour}
            hour={hour}
            days={days}
            slots={slots}
            channelBySlug={channelBySlug}
            templateSlots={templateSlots}
            blocks={blocks}
            channels={channels}
            visibleChannels={visibleChannels}
          />
        ))}
      </div>
    </div>
  )
}

function HourRow({
  hour,
  days,
  slots,
  channelBySlug,
  templateSlots,
  blocks,
  channels,
  visibleChannels
}: {
  hour: number
  days: Date[]
  slots: CalendarSlot[]
  channelBySlug: Map<string, import("./types").CalendarChannel>
  templateSlots: CalendarTemplateSlot[]
  blocks: CalendarBlock[]
  channels: CalendarChannel[]
  visibleChannels: Set<string>
}) {
  return (
    <>
      <div className="border-r border-t border-hairline px-2 py-1 text-right text-[10px] text-ink-muted">
        {formatHour(hour)}
      </div>
      {days.map((d) => (
        <DayHourCell
          key={`${d.toISOString()}-${hour}`}
          date={d}
          hour={hour}
          slots={slots}
          channelBySlug={channelBySlug}
          templateSlots={templateSlots}
          blocks={blocks}
          channels={channels}
          visibleChannels={visibleChannels}
        />
      ))}
    </>
  )
}

function DayHourCell({
  date,
  hour,
  slots,
  channelBySlug,
  templateSlots,
  blocks,
  channels,
  visibleChannels
}: {
  date: Date
  hour: number
  slots: CalendarSlot[]
  channelBySlug: Map<string, import("./types").CalendarChannel>
  templateSlots: CalendarTemplateSlot[]
  blocks: CalendarBlock[]
  channels: CalendarChannel[]
  visibleChannels: Set<string>
}) {
  const cellSlots = slots.filter((s) => {
    if (!visibleChannels.has(s.channel)) return false
    const at = new Date(s.publish_at)
    return sameLocalDay(at, date) && at.getHours() === hour
  })

  // Upload ghosts: render a dashed/faded card at upload_at for every
  // platform-scheduled slot whose upload moment lands in this cell. Even
  // when upload and publish share a cell, we still render both so the
  // user sees the full timeline (one solid card + one dashed ghost).
  const uploadGhosts = slots.filter((s) => {
    if (!s.use_platform_scheduling || !s.upload_at) return false
    if (!visibleChannels.has(s.channel)) return false
    const upload = new Date(s.upload_at)
    return sameLocalDay(upload, date) && upload.getHours() === hour
  })

  const cellTemplates = templateSlots.filter((t) => {
    if (!visibleChannels.has(t.channel)) return false
    // A block makes the template slot unavailable — no open-slot ghost.
    if (instantBlocked(blocks, t.channel, t.publish_at)) return false
    const at = new Date(t.publish_at)
    return sameLocalDay(at, date) && at.getHours() === hour
  })

  // Blocks touching this cell. The full chip renders in the hour the block
  // starts in; later covered hours get a slim continuation marker.
  const cellBlocks = blocks.filter(
    (b) => blockVisible(b, visibleChannels) && blockOverlapsHour(b, date, hour)
  )
  const startingBlocks = cellBlocks.filter((b) => {
    const from = new Date(b.starts_at)
    return sameLocalDay(from, date) ? from.getHours() === hour : hour === 0
  })
  const continuingBlocks = cellBlocks.filter((b) => !startingBlocks.includes(b))

  const dropId = `cell-${date.toISOString().slice(0, 10)}-${hour}`
  const { setNodeRef, isOver } = useDroppable({ id: dropId, data: { date, hour } })
  const isPast = isPastDay(date)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[60px] min-w-0 border-r border-t border-hairline p-1",
        // White for today/future, gray (the grid surface) for past days.
        !isPast && !isOver && "bg-page",
        isPast && "opacity-50",
        isOver && "bg-accent-faded/50"
      )}
    >
      <div className="flex flex-col gap-1">
        {startingBlocks.map((b) => (
          <CalendarBlockChip key={`block-${b.id}`} block={b} channels={channels} />
        ))}
        {continuingBlocks.map((b) => (
          <div
            key={`block-cont-${b.id}`}
            className="flex items-center gap-1 rounded border border-dashed border-hairline px-1.5 py-0.5 text-[10px] text-ink-muted"
            title={`Blocked ${blockRangeLabel(b)}`}
          >
            <Ban className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">Blocked</span>
          </div>
        ))}
        {/* Sort publish + upload entries by their effective time so a
            piece's earlier upload ghost (e.g. 3:13 PM) renders above its
            later publish card (e.g. 3:57 PM) when they share an hour. */}
        {[
          ...cellSlots.map((s) => ({ slot: s, variant: "publish" as const, t: new Date(s.publish_at).getTime() })),
          ...uploadGhosts.map((s) => ({ slot: s, variant: "upload" as const, t: new Date(s.upload_at!).getTime() })),
        ]
          .sort((a, b) => a.t - b.t)
          .map(({ slot: s, variant }) => {
            const ch = channelBySlug.get(s.channel)
            if (!ch) return null
            return (
              <CalendarPieceCard
                key={variant === "upload" ? `upload-${s.id}` : s.id}
                slot={s}
                channel={ch}
                variant={variant}
              />
            )
          })}
        {cellSlots.length === 0 && uploadGhosts.length === 0 && cellTemplates.map((t, idx) => {
          const ch = channelBySlug.get(t.channel)
          if (!ch) return null
          return (
            <div
              key={`tmpl-${idx}`}
              className="rounded border border-dashed border-hairline px-1.5 py-0.5 text-[10px] text-ink-muted"
              title={`Open ${ch.name} slot`}
            >
              {ch.name} · {formatTime(new Date(t.publish_at))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function sameLocalDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

// A day is "past" when its calendar date falls before today's. Today and
// future days stay at full opacity; earlier days are dimmed in the grid.
function isPastDay(date: Date) {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  return startOfDate < startOfToday
}

function formatHour(hour: number) {
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return `${h12}${hour < 12 ? "a" : "p"}`
}

function formatTime(d: Date) {
  const h = d.getHours()
  const m = d.getMinutes()
  const h12 = h % 12 === 0 ? 12 : h % 12
  const period = h < 12 ? "AM" : "PM"
  return `${h12}:${String(m).padStart(2, "0")} ${period}`
}
